import type { Colour } from '@risksmart-app/components/src/utils/colours';
import { colours } from '@risksmart-app/components/src/utils/colours';
import type { RiskRegisterResponseRow } from '@risksmart-app/drizzle/src/types';
import i18n from '@risksmart-app/i18n/src/i18n';
import type {
  Risk_Assessment_Result_Bool_Exp,
  Risk_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetRisksFlatDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mapTrpcRisksToGraphQL } from 'src/hooks/queries/risk/useGetRisksFlat';
import { useGetLabelledFields } from 'src/pages/risks/useGetLabelledFields';
import { useRiskRatingResolver } from 'src/ratings/useRiskRatingResolver';

import { useGetAppetitesGroupedByImpact } from '@/hooks/queries';
import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';
import { useRiskScores } from '@/hooks/useRiskScore';
import type { StatefulTableOptions } from '@/utils/table/hooks/useGetStatelessTableProps';
import type { TableFields } from '@/utils/table/types';

import { UNRATED } from '../../gigawidget/types';
import {
  dateRangeFilter,
  departmentsFilter,
  tagsFilter,
} from '../../gigawidget/util/filterHelpers';
import { getQueryVariables } from '../../my-items/hooks/useGetQueryVariables';
import { createDataSource } from '../createDataSource';
import {
  dashboardDateRangeClickthroughFilter,
  defaultClickthroughFilter,
} from '../dataSourceHelpers';
import {
  prefixedLabel,
  severitySortedField,
  simpleField,
  useRelationshipTableProps,
} from './relationship-helpers';
import type { RiskImpactRatingRow } from './riskImpactRating.flatten';
import { flattenRisksToImpactRatingRows } from './riskImpactRating.flatten';

export default createDataSource({
  group: 'relationship',
  // Visibility is gated in the settings picker on `hasMultipleImpacts` (scoring
  // settings with >= 2 impact categories) — the only signal that per-category
  // impacts exist. That's a hook value unavailable here, and the legacy
  // `risk.subModules.impact` module is unrelated to scoring-settings impacts, so
  // `hasAccess` stays permissive and the picker special-case does the gating.
  hasAccess: () => true,
  parentTypes: [
    Parent_Type_Enum.Risk,
    Parent_Type_Enum.RiskControlledAssessment,
    Parent_Type_Enum.RiskUncontrolledAssessment,
  ],
  documentNode: GetRisksFlatDocument,
  trpcQuery: (trpc, _variables, entityIds) => {
    const queryOptions = trpc.frontend.risk.register.queryOptions({
      entityIds,
    });

    const transformedQueryOptions = {
      ...queryOptions,
      select: (data: RiskRegisterResponseRow[]) => {
        return mapTrpcRisksToGraphQL(data);
      },
    };

    return transformedQueryOptions as UseQueryOptions<unknown, Error>;
  },
  useDefaultVariables: () =>
    ({
      where: useEntityWhereFilter<Risk_Bool_Exp>(Parent_Type_Enum.Risk),
      riskAssessmentResultsWhere: {},
    }) as {
      where: Risk_Bool_Exp;
      riskAssessmentResultsWhere: Risk_Assessment_Result_Bool_Exp;
    },
  useTablePropsHook: (
    data,
    options: StatefulTableOptions<RiskImpactRatingRow>
  ) => {
    const { scores, loading: scoresLoading } = useRiskScores();

    const { data: impactAppetites, loading: appetitesLoading } =
      useGetAppetitesGroupedByImpact({ queryArgs: {} });

    const labelledRisks = useGetLabelledFields(
      data?.risk,
      scores,
      impactAppetites?.impact
    );

    const { resolveImpact } = useRiskRatingResolver('standard', {
      configType: 'risk_assessment',
    });

    const rows = useMemo(
      () =>
        labelledRisks
          ? flattenRisksToImpactRatingRows(labelledRisks, resolveImpact)
          : undefined,
      [labelledRisks, resolveImpact]
    );

    const fields = useMemo<TableFields<RiskImpactRatingRow>>(
      () => ({
        RiskFriendlyId: simpleField(
          prefixedLabel('risk_one', i18n.t('columns.id'))
        ),
        RiskTitle: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_name'))
        ),
        RiskTier: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier'))
        ),
        RiskStatus: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_status'))
        ),
        RiskTreatment: simpleField(
          prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment'))
        ),
        ImpactCategory: simpleField(
          i18n.t('riskImpactRatings.columns.impact_category')
        ),
        InherentImpactRatingLabel: severitySortedField(
          i18n.t('riskImpactRatings.columns.inherent_impact_rating'),
          (item) => item.InherentImpactRatingValue
        ),
        ResidualImpactRatingLabel: severitySortedField(
          i18n.t('riskImpactRatings.columns.residual_impact_rating'),
          (item) => item.ResidualImpactRatingValue
        ),
      }),
      []
    );

    const tableProps = useRelationshipTableProps(
      rows,
      fields,
      i18n.t('risk_impact_rating_other'),
      options
    );

    // Impact-rating rows derive from risk scores + appetites; report those
    // secondary sources as loading rather than "no data" (RSP-6539).
    return {
      ...tableProps,
      isEnrichmentLoading: scoresLoading || appetitesLoading,
    };
  },
  entityNamePlural: 'risk_impact_rating_other',
  entityNameSingular: 'risk_impact_rating_one',
  fields: 'riskImpactRatings.fields',
  dashboardFilterConfig: {
    tagsFilter: (tags) => ({ where: { tags: tagsFilter(tags) } }),
    departmentsFilter: (departments) => ({
      where: { departments: departmentsFilter(departments) },
    }),
    dateFilter: (dateRange, precision) => ({
      where: { CreatedAtTimestamp: dateRangeFilter(dateRange, precision) },
    }),
    dateClickthroughFilter:
      dashboardDateRangeClickthroughFilter('CreatedAtTimestamp'),
    ownershipFilter: (myItemsFilters, userId) => ({
      where: getQueryVariables(myItemsFilters, userId).riskFilterConditions,
    }),
  },
  categoryGetters: [
    // Risk-side getters mirror the other "by Risks" relational sources
    // (Actions by Risks, Controls by Risks): Risk ID / name / tier / status /
    // treatment, grouped first under the "Risks" heading.
    {
      id: 'riskFriendlyId',
      entityGroup: 'risk_one' as const,
      name: () => prefixedLabel('risk_one', i18n.t('columns.id')),
      categoryGetter: (item) => item.RiskFriendlyId || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskFriendlyId'),
    },
    {
      id: 'riskTitle',
      entityGroup: 'risk_one' as const,
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_name')),
      categoryGetter: (item) => item.RiskTitle || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTitle'),
    },
    {
      id: 'riskTier',
      entityGroup: 'risk_one' as const,
      name: () => prefixedLabel('risk_one', i18n.t('risks.columns.risk_tier')),
      categoryGetter: (item) => item.RiskTier || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTier'),
    },
    {
      id: 'riskStatus',
      entityGroup: 'risk_one' as const,
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_status')),
      categoryGetter: (item) => item.RiskStatus || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskStatus'),
    },
    {
      id: 'riskTreatment',
      entityGroup: 'risk_one' as const,
      name: () =>
        prefixedLabel('risk_one', i18n.t('risks.columns.risk_treatment')),
      categoryGetter: (item) => item.RiskTreatment || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('RiskTreatment'),
    },
    {
      id: 'impactCategory',
      entityGroup: 'impact_rating_one' as const,
      name: () => i18n.t('riskImpactRatings.columns.impact_category'),
      categoryGetter: (item) => item.ImpactCategory || UNRATED,
      clickthroughFilter: defaultClickthroughFilter('ImpactCategory'),
    },
    {
      id: 'inherentImpactRating',
      entityGroup: 'impact_rating_one' as const,
      name: () => i18n.t('riskImpactRatings.columns.inherent_impact_rating'),
      categoryGetter: (item) => ({
        key: item.InherentImpactRatingLabel || UNRATED,
        label: item.InherentImpactRatingLabel || UNRATED,
        sortKey: item.InherentImpactRatingValue?.toString(),
      }),
      clickthroughFilter: defaultClickthroughFilter(
        'InherentImpactRatingLabel',
        { unratedValue: UNRATED }
      ),
      categoryOverrideFunction: (category) => {
        const colour = category.data[0]?.InherentImpactRatingColour;

        return {
          title: category.data[0]?.InherentImpactRatingLabel || UNRATED,
          color:
            colours[(colour ?? 'light-grey') as Colour]?.backgroundColor ??
            colour ??
            undefined,
        };
      },
    },
    {
      id: 'residualImpactRating',
      entityGroup: 'impact_rating_one' as const,
      name: () => i18n.t('riskImpactRatings.columns.residual_impact_rating'),
      categoryGetter: (item) => ({
        key: item.ResidualImpactRatingLabel || UNRATED,
        label: item.ResidualImpactRatingLabel || UNRATED,
        sortKey: item.ResidualImpactRatingValue?.toString(),
      }),
      clickthroughFilter: defaultClickthroughFilter(
        'ResidualImpactRatingLabel',
        { unratedValue: UNRATED }
      ),
      categoryOverrideFunction: (category) => {
        const colour = category.data[0]?.ResidualImpactRatingColour;

        return {
          title: category.data[0]?.ResidualImpactRatingLabel || UNRATED,
          color:
            colours[(colour ?? 'light-grey') as Colour]?.backgroundColor ??
            colour ??
            undefined,
        };
      },
    },
  ],
});
